import { SLIDESHOW_DEFAULTS } from 'ts/common/components/slideshows/slideshowUtils';

/**
 * NLE-style overlap timeline: adjacent segments share an overlap window of length T
 * at each boundary. Segment i starts at start[i]; overlap between i and i+1 is
 * [start[i+1], start[i+1] + T).
 */

export interface OverlapTimelineLayout {
    segmentStarts: number[];
    segmentDurations: number[];
    /** Effective overlap used (may be clamped from requested transition duration) */
    overlapDuration: number;
    /** Effective overlap at each slide boundary; looping layouts include the final-to-first boundary. */
    boundaryOverlapDurations: number[];
    loop: boolean;
    loopRestartOffset: number;
    totalDuration: number;
}

export interface ActiveSlidesAtTime {
    /** Outgoing slide index during overlap; sole slide when secondary is null */
    primaryIndex: number;
    /** Incoming slide index during overlap */
    secondaryIndex: number | null;
    /** 0 = outgoing only, 1 = incoming only (linear crossfade weight for incoming) */
    blend: number;
}

export interface SlideshowTransitionConfig {
    type: SpApi.SlideshowTransition;
    duration: number;
}

export function getSlideDurationMs(slide: SpApi.ISlideshowSlide): number {
    return slide.duration ?? SLIDESHOW_DEFAULTS.slide.duration;
}

function getTransitionDurationMs(duration: number | null | undefined): number {
    return duration ?? SLIDESHOW_DEFAULTS.slide.transitionDuration;
}

export function getSlideTransition(slide: SpApi.ISlideshowSlide): SlideshowTransitionConfig {
    const type: SpApi.SlideshowTransition = slide.transition ?? SLIDESHOW_DEFAULTS.slide.transition;
    const duration = slide.transitionDuration ?? SLIDESHOW_DEFAULTS.slide.transitionDuration;

    return {
        type,
        duration: type === 'cut' ? 0 : getTransitionDurationMs(duration)
    };
}

export function getBoundaryTransitionDurations(slideshow: SpApi.IEventSlideshow): number[] {
    const { slides } = slideshow;
    const boundaryCount =
        slideshow.slideshowLoop && slides.length > 1 ? slides.length : slides.length - 1;

    return slides.slice(0, boundaryCount).map((slide, index) => {
        const requestedDuration = getSlideTransition(slide).duration;
        const currentDuration = getSlideDurationMs(slide);
        const nextDuration = getSlideDurationMs(slides[(index + 1) % slides.length]);

        return clampOverlapToSegmentBoundary(requestedDuration, currentDuration, nextDuration);
    });
}

export function getSlideSeekTimes(slideshow: SpApi.IEventSlideshow): number[] {
    const layout = buildOverlapTimeline(
        slideshow.slides.map(getSlideDurationMs),
        getBoundaryTransitionDurations(slideshow),
        { loop: slideshow.slideshowLoop }
    );

    return layout.segmentStarts.map((start, index) => {
        if (index === 0) {
            return start;
        }

        return start + layout.boundaryOverlapDurations[index - 1];
    });
}

/**
 * Builds segment start times for uniform overlap between consecutive slides.
 * start[i+1] = start[i] + D[i] - T
 * Total duration = start[n-1] + D[n-1] = sum(D) - (n-1)*T
 */
export function buildOverlapTimeline(
    segmentDurations: number[],
    requestedOverlapDuration: number | number[],
    options: { loop: boolean }
): OverlapTimelineLayout {
    const n = segmentDurations.length;
    const loop = n > 1 && Boolean(options.loop);
    const boundaryCount = Math.max(0, loop ? n : n - 1);

    const boundaryOverlapDurations = Array.isArray(requestedOverlapDuration)
        ? clampBoundaryOverlapsToSegments(segmentDurations, requestedOverlapDuration, loop)
        : new Array(boundaryCount).fill(
              clampOverlapToSegments(segmentDurations, requestedOverlapDuration, loop)
          );
    const overlapDuration = Math.max(0, ...boundaryOverlapDurations);
    const loopRestartOffset = loop ? (boundaryOverlapDurations[n - 1] ?? 0) / 2 : 0;

    if (n === 0) {
        return {
            segmentStarts: [],
            segmentDurations: [],
            overlapDuration,
            boundaryOverlapDurations,
            loop,
            loopRestartOffset,
            totalDuration: 0
        };
    }

    if (n === 1) {
        return {
            segmentStarts: [0],
            segmentDurations: [...segmentDurations],
            overlapDuration,
            boundaryOverlapDurations,
            loop,
            loopRestartOffset,
            totalDuration: segmentDurations[0]
        };
    }

    const starts: number[] = [0];

    for (let i = 1; i < n; i++) {
        starts.push(starts[i - 1] + segmentDurations[i - 1] - boundaryOverlapDurations[i - 1]);
    }

    const totalDuration = starts[n - 1] + segmentDurations[n - 1];

    return {
        segmentStarts: starts,
        segmentDurations: [...segmentDurations],
        overlapDuration,
        boundaryOverlapDurations,
        loop,
        loopRestartOffset,
        totalDuration
    };
}

/** Ensures adjacent transition windows cannot overlap within a segment. */
export function clampOverlapToSegments(
    segmentDurations: number[],
    overlapDuration: number,
    loop: boolean
): number {
    const n = segmentDurations.length;

    if (n <= 1 || overlapDuration <= 0) {
        return 0;
    }

    let maxT = overlapDuration;

    const boundaryCount = loop ? n : n - 1;

    for (let i = 0; i < boundaryCount; i++) {
        maxT = clampOverlapToSegmentBoundary(
            maxT,
            segmentDurations[i],
            segmentDurations[(i + 1) % n]
        );
    }

    return Math.max(0, maxT);
}

function clampOverlapToSegmentBoundary(
    overlapDuration: number,
    currentDuration: number,
    nextDuration: number
): number {
    const maxOverlap = Math.max(0, Math.min(currentDuration, nextDuration) / 2);

    return Math.max(0, Math.min(overlapDuration, maxOverlap));
}

function clampBoundaryOverlapsToSegments(
    segmentDurations: number[],
    overlapDurationsByBoundary: number[],
    loop: boolean
): number[] {
    const boundaryCount = Math.max(0, loop ? segmentDurations.length : segmentDurations.length - 1);

    return segmentDurations.slice(0, boundaryCount).map((duration, index) => {
        const requestedOverlap = overlapDurationsByBoundary[index] ?? 0;

        return clampOverlapToSegmentBoundary(
            requestedOverlap,
            duration,
            segmentDurations[(index + 1) % segmentDurations.length]
        );
    });
}

/**
 * Whether playback may render `time` given which slide images have finished
 * preloading. Both the outgoing slide and, during a transition, the incoming
 * slide must be ready — otherwise the caller should hold at the last position
 * (buffer) rather than starting a transition into an unloaded image.
 */
export function canAdvanceToTime(
    time: number,
    layout: OverlapTimelineLayout,
    readyIndices: ReadonlySet<number>,
    playbackDurationMs = layout.totalDuration
): boolean {
    const active = getActiveSlidesAtTime(time, layout, playbackDurationMs);

    return (
        readyIndices.has(active.primaryIndex) &&
        (active.secondaryIndex === null || readyIndices.has(active.secondaryIndex))
    );
}

export function getActiveSlidesAtTime(
    time: number,
    layout: OverlapTimelineLayout,
    playbackDurationMs = layout.totalDuration
): ActiveSlidesAtTime {
    const {
        segmentStarts: starts,
        segmentDurations: D,
        overlapDuration: uniformOverlapDuration,
        boundaryOverlapDurations,
        loop,
        totalDuration
    } = layout;
    const n = D.length;

    if (n === 0) {
        return { primaryIndex: 0, secondaryIndex: null, blend: 0 };
    }

    const t = resolvePlaybackTime(time, layout, playbackDurationMs);

    // Music-only region past the last slide (within a longer soundtrack cycle).
    if (t > totalDuration) {
        return { primaryIndex: n - 1, secondaryIndex: null, blend: 0 };
    }

    for (let i = 0; i < n - 1; i++) {
        const overlapStart = starts[i + 1];

        if (t < overlapStart) {
            return { primaryIndex: i, secondaryIndex: null, blend: 0 };
        }

        const overlapDuration = boundaryOverlapDurations[i] ?? uniformOverlapDuration;

        if (overlapDuration > 0 && t < overlapStart + overlapDuration) {
            return {
                primaryIndex: i,
                secondaryIndex: i + 1,
                blend: Math.min(1, Math.max(0, (t - overlapStart) / overlapDuration))
            };
        }
    }

    // Last→first crossfade only when the loop cycle is the slide timeline itself. When the
    // soundtrack is longer, we finish the last slide, play out the music on an empty frame,
    // then wrap — so we must not start blending into slide 0 at the slide-content boundary.
    if (loop && playbackDurationMs <= totalDuration) {
        const overlapDuration = boundaryOverlapDurations[n - 1] ?? uniformOverlapDuration;
        const overlapStart = totalDuration - overlapDuration;

        if (overlapDuration > 0 && t >= overlapStart) {
            return {
                primaryIndex: n - 1,
                secondaryIndex: 0,
                blend: Math.min(1, Math.max(0, (t - overlapStart) / overlapDuration))
            };
        }
    }

    return { primaryIndex: n - 1, secondaryIndex: null, blend: 0 };
}

/**
 * Maps raw playback time to the effective timeline position for rendering.
 *
 * `playbackDurationMs` (defaults to slide `totalDuration`) is the playable length — the longer
 * of slide content and soundtrack. When not looping, time is clamped to that ceiling. When
 * looping (Continuous Loop), time wraps on that same ceiling so each cycle includes any
 * music that extends past the slides.
 *
 * The last→first slide seam (`loopRestartOffset`) only applies when the cycle equals the
 * slide timeline; a longer soundtrack cycle restarts at 0 after the music ends.
 */
export function resolvePlaybackTime(
    timeMs: number,
    layout: Pick<OverlapTimelineLayout, 'loop' | 'loopRestartOffset' | 'totalDuration'>,
    playbackDurationMs = layout.totalDuration
): number {
    const { loop, loopRestartOffset, totalDuration } = layout;
    const cycleDurationMs = Math.max(totalDuration, playbackDurationMs);

    if (loop) {
        if (cycleDurationMs <= 0) {
            return 0;
        }

        const restartOffset = playbackDurationMs <= totalDuration ? loopRestartOffset : 0;

        return wrapTime(timeMs, cycleDurationMs, restartOffset);
    }

    if (cycleDurationMs <= 0) {
        return 0;
    }

    return Math.max(0, Math.min(cycleDurationMs, timeMs));
}

/**
 * Keeps the playhead on the same slide when the overlap layout changes while editing.
 *
 * Continuous Loop's normal `resolvePlaybackTime` wraps with modulo — so shortening a slide
 * until `totalDuration` equals (or divides) the current time snaps the playhead to t=0.
 * While paused we instead preserve the slide under the playhead and clamp into that slide's
 * clear on-screen window (after the incoming transition, before the outgoing one). Clamping
 * to `start + duration` lands at the end of the outgoing transition, where the next slide
 * is already primary.
 */
/**
 * The window where slide `index` is the only slide on screen — from the end of its incoming
 * transition to the start of its outgoing one. Anywhere inside it the slide renders at full
 * opacity, so it's the range to park the playhead in when a slide must be shown on its own.
 *
 * A slide fully covered by transitions has an empty window; `startMs` is still the best
 * position, since blend is 0 there and the slide is fully visible.
 */
export function getSlideClearWindow(
    layout: OverlapTimelineLayout,
    index: number
): { startMs: number; endExclusiveMs: number } {
    const start = layout.segmentStarts[index] ?? 0;
    const startMs = index === 0 ? start : start + (layout.boundaryOverlapDurations[index - 1] ?? 0);
    const isLast = index === layout.segmentDurations.length - 1;

    if (!isLast) {
        return {
            startMs,
            endExclusiveMs: layout.segmentStarts[index + 1] ?? layout.totalDuration
        };
    }

    return {
        startMs,
        endExclusiveMs: layout.loop
            ? layout.totalDuration - (layout.boundaryOverlapDurations[index] ?? 0)
            : layout.totalDuration
    };
}

export function preservePlaybackTimeAcrossLayoutChange(
    timeMs: number,
    previousLayout: OverlapTimelineLayout,
    nextLayout: OverlapTimelineLayout,
    previousPlaybackDurationMs = previousLayout.totalDuration,
    nextPlaybackDurationMs = nextLayout.totalDuration
): number {
    if (previousLayout.segmentDurations.length === 0 || nextLayout.segmentDurations.length === 0) {
        return resolvePlaybackTime(timeMs, nextLayout, nextPlaybackDurationMs);
    }

    const { primaryIndex } = getActiveSlidesAtTime(
        timeMs,
        previousLayout,
        previousPlaybackDurationMs
    );

    if (primaryIndex < 0 || primaryIndex >= nextLayout.segmentStarts.length) {
        return resolvePlaybackTime(timeMs, nextLayout, nextPlaybackDurationMs);
    }

    const previousClearStart = getSlideClearWindow(previousLayout, primaryIndex).startMs;
    const { startMs: nextClearStart, endExclusiveMs: nextClearEndExclusive } = getSlideClearWindow(
        nextLayout,
        primaryIndex
    );
    const nextClearDurationMs = Math.max(0, nextClearEndExclusive - nextClearStart);
    // If the slide is entirely covered by transitions, park at clearStart (seek time) —
    // blend is 0 there so the edited slide is still fully visible.
    const maxTime =
        nextClearDurationMs > 0 ? nextClearStart + nextClearDurationMs - 1 : nextClearStart;
    const offsetFromClear = Math.max(0, timeMs - previousClearStart);
    let clampedTime = Math.max(nextClearStart, Math.min(nextClearStart + offsetFromClear, maxTime));

    const cycleDurationMs = Math.max(nextLayout.totalDuration, nextPlaybackDurationMs);

    if (cycleDurationMs <= 0) {
        return 0;
    }

    // Loop cycles use [0, cycleDurationMs); landing exactly on the cycle length wraps to 0.
    if (nextLayout.loop && clampedTime >= cycleDurationMs) {
        clampedTime = Math.max(0, cycleDurationMs - 1);
    }

    if (!nextLayout.loop) {
        return Math.max(0, Math.min(cycleDurationMs, clampedTime));
    }

    return clampedTime;
}

function wrapTime(t: number, totalDuration: number, restartOffset = 0): number {
    if (totalDuration <= 0) {
        return 0;
    }

    if (restartOffset > 0 && t >= totalDuration) {
        const loopDuration = Math.max(1, totalDuration - restartOffset);

        return restartOffset + ((t - totalDuration) % loopDuration);
    }

    let wrapped = t % totalDuration;

    if (wrapped < 0) {
        wrapped += totalDuration;
    }

    return wrapped;
}
